import api from '@/api/client'

/**
 * U1/S1 — dimensioni-contatto Party (consenso, lifecycle, owner, attività, deal, documenti).
 * Alimentano la scheda 360° dell'Anagrafica Unica.
 */
export type ConsentChannel = 'EMAIL' | 'WHATSAPP' | 'PHONE' | 'SMS' | 'PROFILING' | 'ALL'
export type ConsentStatus = 'GRANTED' | 'DENIED' | 'PENDING' | 'REVOKED'
export type LifecycleStage = 'SUBSCRIBER' | 'LEAD' | 'MQL' | 'SQL' | 'OPPORTUNITY' | 'CUSTOMER' | 'EVANGELIST' | 'INACTIVE' | 'OTHER'
export type ActivityType = 'NOTE' | 'EMAIL' | 'CALL' | 'MEETING' | 'WHATSAPP' | 'TASK' | 'SYSTEM' | 'FIELD_CHANGE' | 'CONSENT_CHANGE' | 'STAGE_CHANGE' | 'IMPORT' | 'MERGE'
export type DealStage = 'NEW' | 'QUALIFIED' | 'PROPOSAL' | 'NEGOTIATION' | 'WON' | 'LOST'

export interface PartyConsent { id: string; channel: ConsentChannel; status: ConsentStatus; source?: string | null; grantedAt?: string | null; revokedAt?: string | null }
export interface PartyActivity { id: string; type: ActivityType; summary: string; body?: string | null; actorUserId?: string | null; channel?: string | null; occurredAt: string; meta?: { changes?: { field: string; label: string; old: any; new: any }[] } | null }
export interface PartyDeal { id: string; partyId: string; title: string; stage: DealStage; valueEuroCents?: number | null; ownerId?: string | null; nextAction?: string | null; nextActionAt?: string | null; expectedCloseDate?: string | null; closedAt?: string | null; lostReason?: string | null }
export interface PartyDocument { id: string; fileKey: string; fileName: string; mimeType?: string | null; sizeBytes?: number | null; category?: string | null; createdAt: string }

export interface ExternalDoctorInput {
  firstName: string
  lastName?: string
  facility?: string
  city?: string
  province?: string
  countryCode?: string
}

export interface ReconciliationCandidate {
  partyId: string
  name: string
  city: string | null
  facility: string | null
  kind: string
}

export const partyDimensionsApi = {
  /** Registra (o riusa) un medico esterno alla piattaforma nell'anagrafica unica. */
  createExternalDoctor: (data: ExternalDoctorInput) =>
    api.post<{ partyId: string; created: boolean }>('/v1/party/external-doctors', data).then(r => r.data),

  /** Schede anagrafiche preesistenti (senza account) che corrispondono al mio nome. */
  reconciliationCandidates: (firstName: string, lastName: string) =>
    api.get<{ candidates: ReconciliationCandidate[]; total: number }>(
      '/v1/party/reconciliation/candidates',
      { params: { firstName, lastName } },
    ).then(r => r.data),

  /** Collega la scheda scelta al mio account (solo su conferma esplicita). */
  confirmReconciliation: (partyId: string) =>
    api.post<{ ok: boolean; partyId: string; doctorGranted: boolean }>(
      '/v1/party/reconciliation/confirm',
      { partyId },
    ).then(r => r.data),
  setConsent: (partyId: string, channel: ConsentChannel, status: ConsentStatus, source?: string) =>
    api.put<PartyConsent>(`/v1/party/persons/${partyId}/consents`, { channel, status, source }).then(r => r.data),
  setOwner: (partyId: string, ownerId: string | null) =>
    api.patch(`/v1/party/persons/${partyId}/owner`, { ownerId }).then(r => r.data),
  setLifecycle: (partyId: string, lifecycleStage: LifecycleStage | null) =>
    api.patch(`/v1/party/persons/${partyId}/lifecycle`, { lifecycleStage }).then(r => r.data),
  addActivity: (partyId: string, summary: string, type: ActivityType = 'NOTE', body?: string) =>
    api.post<PartyActivity>(`/v1/party/persons/${partyId}/activities`, { summary, type, body }).then(r => r.data),
  listActivities: (partyId: string, type?: ActivityType, limit = 100) =>
    api.get<PartyActivity[]>(`/v1/party/persons/${partyId}/activities`, { params: { ...(type ? { type } : {}), limit } }).then(r => r.data),
  // edit anagrafica (campi identità) — persona o azienda; il backend registra lo storico
  updatePerson: (partyId: string, data: Record<string, unknown>) =>
    api.patch(`/v1/party/persons/${partyId}`, data).then(r => r.data),
  updateOrganization: (partyId: string, data: Record<string, unknown>) =>
    api.patch(`/v1/party/organizations/${partyId}`, data).then(r => r.data),
  createDeal: (data: { partyId: string; title: string; stage?: DealStage; valueEuroCents?: number; nextAction?: string }) =>
    api.post<PartyDeal>(`/v1/party/deals`, data).then(r => r.data),
  updateDeal: (dealId: string, data: Partial<{ title: string; stage: DealStage; valueEuroCents: number | null; nextAction: string | null; lostReason: string | null }>) =>
    api.patch<PartyDeal>(`/v1/party/deals/${dealId}`, data).then(r => r.data),
  // U3 — contatti (contact points) dalla scheda
  addContactPoint: (partyId: string, type: string, value: string, isPrimary = false) =>
    api.post(`/v1/party/${partyId}/contact-points`, { type, value, isPrimary }).then(r => r.data),
  deleteContactPoint: (partyId: string, id: string) =>
    api.delete(`/v1/party/${partyId}/contact-points/${id}`).then(r => r.data),
  // U4 — ruoli/qualità dalla scheda
  // startDate → grantedAt (inizio validità), endDate → expiresAt (fine, opzionale = in corso)
  grantRole: (partyId: string, role: string, opts?: { startDate?: string; endDate?: string }) =>
    api.post(`/v1/party/persons/${partyId}/roles`, {
      role,
      grantedAt: opts?.startDate || undefined,
      expiresAt: opts?.endDate || undefined,
    }).then(r => r.data),
  revokeRole: (partyId: string, role: string) =>
    api.delete(`/v1/party/persons/${partyId}/roles/${role}`).then(r => r.data),
}

export interface PartyStats {
  totals: { persons: number; organizations: number; total: number }
  byLifecycleStage: { stage: string; count: number }[]
  byConsent: { channel: string; status: string; count: number }[]
  bySource: { source: string; count: number }[]
  byRole: { role: string; count: number }[]
  byDealStage: { stage: string; count: number }[]
  completeness: { withEmail: number; emailCoverage: number }
  dataQuality: { dedupReview: number; noContact?: number; unreachable?: number; duplicateEmailClusters?: number }
}
export const partyStatsApi = {
  get: () => api.get<PartyStats>('/v1/party/stats').then(r => r.data),
}

// U19 — custom fields (definizioni attributi a runtime)
export interface AttributeDef {
  id: string; scope: 'ORG' | 'PERSON'; classifier: string; key: string; label: string
  kind: 'bool' | 'text' | 'number' | 'enum'; options: string[]; unit?: string | null; filterable: boolean
}
export const partyAttributeDefsApi = {
  list: () => api.get<AttributeDef[]>('/v1/party/attribute-defs').then(r => r.data),
  create: (def: Omit<AttributeDef, 'id'>) => api.post<AttributeDef>('/v1/party/attribute-defs', def).then(r => r.data),
  remove: (id: string) => api.delete(`/v1/party/attribute-defs/${id}`).then(r => r.data),
}
export const ORG_TYPE_OPTIONS = ['DIVE_CENTER', 'DIVE_RESORT', 'ASSOCIATION', 'TRAVEL_AGENCY', 'INSURANCE_PROVIDER', 'SCHOOL', 'MARINE_BIOLOGY_CENTER', 'BUSINESS', 'HOSPITAL', 'POOL', 'CAR_RENTAL', 'BOAT_RENTAL', 'RESEARCH_CENTER', 'ACADEMY'] as const
export const PERSON_CAP_OPTIONS = ['INSTRUCTOR', 'DIVEMASTER', 'DIVE_GUIDE', 'DIVE_DOCTOR', 'DIVE_PHOTOGRAPHER', 'TECHNICAL_DIVER', 'RESEARCHER', 'TOURISM_OPERATOR', 'RENTAL_OPERATOR'] as const

// U8 — import CSV/Excel
export interface ImportReport { total: number; created: number; updated: number; errors: { row: number; error: string }[] }
export const partyImportApi = {
  run: (type: 'PERSON' | 'ORGANIZATION', rows: Record<string, any>[]) =>
    api.post<ImportReport>('/v1/party/import', { type, rows }).then(r => r.data),
}
export const IMPORT_FIELDS: Record<'PERSON' | 'ORGANIZATION', { key: string; label: string; required?: boolean }[]> = {
  PERSON: [
    { key: 'firstName', label: 'Nome', required: true },
    { key: 'lastName', label: 'Cognome', required: true },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Telefono' },
    { key: 'mobilePhone', label: 'Cellulare' },
    { key: 'fiscalCode', label: 'Codice fiscale' },
    { key: 'nationalityCode', label: 'Nazionalità (ISO2)' },
  ],
  ORGANIZATION: [
    { key: 'name', label: 'Ragione sociale', required: true },
    { key: 'type', label: 'Tipo (DIVE_CENTER…)' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Telefono' },
    { key: 'website', label: 'Sito web' },
    { key: 'city', label: 'Città' },
    { key: 'countryCode', label: 'Paese (ISO2)' },
    { key: 'address', label: 'Indirizzo' },
  ],
}

// U17 — documenti / allegati
export const partyDocumentsApi = {
  list: (partyId: string) => api.get<PartyDocument[]>(`/v1/party/persons/${partyId}/documents`).then(r => r.data),
  upload: (partyId: string, file: File, category?: string) => {
    const qs = new URLSearchParams({ filename: file.name, mimeType: file.type || 'application/octet-stream', sizeBytes: String(file.size), ...(category ? { category } : {}) })
    return api.post<PartyDocument>(`/v1/party/persons/${partyId}/documents/upload?${qs}`, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } }).then(r => r.data)
  },
  download: async (partyId: string, doc: PartyDocument) => {
    const res = await api.get(`/v1/party/persons/${partyId}/documents/${doc.id}/download`, { responseType: 'blob' })
    const url = URL.createObjectURL(res.data as Blob)
    const a = document.createElement('a'); a.href = url; a.download = doc.fileName; document.body.appendChild(a); a.click(); a.remove()
    URL.revokeObjectURL(url)
  },
  remove: (partyId: string, docId: string) => api.delete(`/v1/party/persons/${partyId}/documents/${docId}`).then(r => r.data),
}

// U6 — dedup / merge
export interface DedupMember {
  id: string; type: 'PERSON' | 'ORGANIZATION'; source: string; lifecycleStage?: string | null
  createdAt: string; iamUserId?: string | null; name: string
  counts: { contactPoints: number; deals: number; activities: number; memberships: number; members: number }
}
export interface DedupCluster { members: DedupMember[] }
export const partyDedupApi = {
  candidates: () => api.get<{ clusters: DedupCluster[]; total: number }>('/v1/party/dedup/candidates').then(r => r.data),
  merge: (survivorId: string, mergedId: string) =>
    api.post<{ ok: boolean }>('/v1/party/merge', { survivorId, mergedId }).then(r => r.data),
}

// Presenza social (PartyPresence) gestita dalla scheda 360°
export interface PartyPresence {
  id?: string; platform: string; profileUrl?: string | null; handle?: string | null; isOfficialPage?: boolean
}
export const partyPresenceApi = {
  list: (partyId: string) => api.get<PartyPresence[]>(`/v1/party/${partyId}/presence`).then(r => r.data),
  upsert: (partyId: string, platform: string, body: { profileUrl?: string; handle?: string; isOfficialPage?: boolean }) =>
    api.put<PartyPresence>(`/v1/party/${partyId}/presence/${platform}`, body).then(r => r.data),
  remove: (partyId: string, platform: string) => api.delete(`/v1/party/${partyId}/presence/${platform}`).then(r => r.data),
}
export const PRESENCE_PLATFORMS = [
  'INSTAGRAM', 'FACEBOOK', 'LINKEDIN', 'X_TWITTER', 'YOUTUBE', 'TIKTOK', 'PINTEREST', 'WHATSAPP', 'TELEGRAM',
  'STRAVA', 'DIVEBOARD', 'DIVELOGS_DE', 'GARMIN_CONNECT', 'SUUNTO_APP', 'GITHUB', 'VIMEO', 'OTHER',
] as const
export const PRESENCE_LABEL: Record<string, string> = {
  INSTAGRAM: 'Instagram', FACEBOOK: 'Facebook', LINKEDIN: 'LinkedIn', X_TWITTER: 'X (Twitter)', YOUTUBE: 'YouTube',
  TIKTOK: 'TikTok', PINTEREST: 'Pinterest', WHATSAPP: 'WhatsApp', TELEGRAM: 'Telegram', STRAVA: 'Strava',
  DIVEBOARD: 'Diveboard', DIVELOGS_DE: 'Divelogs.de', GARMIN_CONNECT: 'Garmin Connect', SUUNTO_APP: 'Suunto',
  GITHUB: 'GitHub', VIMEO: 'Vimeo', OTHER: 'Altro',
}

// Indirizzi (PartyAddress) gestiti dalla scheda 360°
export interface PartyAddress {
  id: string; type: string; street?: string | null; streetNumber?: string | null
  city?: string | null; province?: string | null; postalCode?: string | null; countryCode?: string | null; isMain?: boolean
}
export const partyAddressesApi = {
  list: (partyId: string) => api.get<PartyAddress[]>(`/v1/party/${partyId}/addresses`).then(r => r.data),
  add: (partyId: string, body: Partial<PartyAddress>) => api.post<PartyAddress>(`/v1/party/${partyId}/addresses`, body).then(r => r.data),
  update: (partyId: string, id: string, body: Partial<PartyAddress>) => api.patch<PartyAddress>(`/v1/party/${partyId}/addresses/${id}`, body).then(r => r.data),
  remove: (partyId: string, id: string) => api.delete(`/v1/party/${partyId}/addresses/${id}`).then(r => r.data),
}
export const ADDRESS_TYPES = ['HOME', 'WORK', 'REGISTERED_OFFICE', 'OPERATIONAL', 'OTHER'] as const
export const ADDRESS_TYPE_LABEL: Record<string, string> = {
  HOME: 'Residenza', WORK: 'Lavoro', REGISTERED_OFFICE: 'Sede legale', OPERATIONAL: 'Sede operativa', OTHER: 'Altro',
}

// U5 — appartenenze Persona↔Azienda
export interface OrgMember {
  id: string; memberRole: string; isPrimary?: boolean; personPartyId: string; orgPartyId: string; startDate?: string | null; endDate?: string | null
  person?: { person?: { firstName?: string; lastName?: string; displayName?: string } | null } | null
}
export interface PersonMembership {
  id: string; memberRole: string; isPrimary?: boolean; personPartyId: string; orgPartyId: string; startDate?: string | null; endDate?: string | null
  organization?: { organization?: { name?: string } | null } | null
}
export const partyMembersApi = {
  listMembers: (orgId: string) => api.get<OrgMember[]>(`/v1/party/${orgId}/members`).then(r => r.data),
  listMemberships: (personId: string) => api.get<PersonMembership[]>(`/v1/party/${personId}/memberships`).then(r => r.data),
  addMember: (orgId: string, body: { personPartyId: string; memberRole: string; isPrimary?: boolean; startDate?: string; endDate?: string }) =>
    api.post(`/v1/party/${orgId}/members`, body).then(r => r.data),
  endMember: (orgId: string, memberId: string) => api.delete(`/v1/party/${orgId}/members/${memberId}`).then(r => r.data),
}
// picker leggero per le appartenenze (riusa le liste del catalogo)
export const partySearchApi = {
  persons: (q: string) => api.get<{ data: { id: string; person?: { firstName?: string; lastName?: string; displayName?: string } | null }[] }>('/v1/party/persons', { params: { q, limit: 8 } }).then(r => r.data.data),
  organizations: (q: string) => api.get<{ data: { id: string; organization?: { name?: string } | null }[] }>('/v1/party/organizations', { params: { q, limit: 8 } }).then(r => r.data.data),
}
export const MEMBER_ROLES = [
  'OWNER', 'LEGAL_OWNER', 'MANAGER', 'BOARD_MEMBER',
  'INSTRUCTOR', 'DIVEMASTER', 'GUIDE', 'BOAT_CAPTAIN', 'SAFETY_DIVER',
  'BIOLOGIST', 'MARINE_BIOLOGIST', 'RESEARCHER', 'PHOTOGRAPHER', 'VIDEOGRAPHER', 'NATURALIST',
  'STAFF', 'TRAVEL_COORDINATOR', 'VOLUNTEER', 'FREELANCE', 'CONTRACTOR',
  'STUDENT', 'APPRENTICE',
  'MEMBER', 'HONORARY_MEMBER', 'JUNIOR_MEMBER', 'SUPPORTER',
  'PARTNER', 'SUPPLIER', 'CONSULTANT',
] as const
export const MEMBER_ROLE_LABEL: Record<string, string> = {
  OWNER: 'Titolare', LEGAL_OWNER: 'Rappr. legale', MANAGER: 'Direttore', BOARD_MEMBER: 'Consiglio direttivo',
  INSTRUCTOR: 'Istruttore', DIVEMASTER: 'Divemaster', GUIDE: 'Guida', BOAT_CAPTAIN: 'Comandante', SAFETY_DIVER: 'Safety diver',
  BIOLOGIST: 'Biologo', MARINE_BIOLOGIST: 'Biologo marino', RESEARCHER: 'Ricercatore', PHOTOGRAPHER: 'Fotografo', VIDEOGRAPHER: 'Videomaker', NATURALIST: 'Naturalista',
  STAFF: 'Staff', TRAVEL_COORDINATOR: 'Coord. viaggi', VOLUNTEER: 'Volontario', FREELANCE: 'Freelance', CONTRACTOR: 'Collaboratore',
  STUDENT: 'Allievo', APPRENTICE: 'Apprendista',
  MEMBER: 'Socio', HONORARY_MEMBER: 'Socio onorario', JUNIOR_MEMBER: 'Socio junior', SUPPORTER: 'Sostenitore',
  PARTNER: 'Partner', SUPPLIER: 'Fornitore', CONSULTANT: 'Consulente',
}

// U10 — segmenti / viste salvate
export interface PartySegment { id: string; name: string; description?: string | null; kind: 'DYNAMIC' | 'STATIC'; definition?: Record<string, unknown> | null }
export const partySegmentsApi = {
  list: () => api.get<PartySegment[]>('/v1/party/segments').then(r => r.data),
  create: (data: { name: string; kind?: 'DYNAMIC' | 'STATIC'; definition?: Record<string, unknown> }) =>
    api.post<PartySegment>('/v1/party/segments', data).then(r => r.data),
  remove: (id: string) => api.delete(`/v1/party/segments/${id}`).then(r => r.data),
}

// Qualità assegnabili dalla scheda (ruoli professionali/diving, non gli organizzativi/commerciali)
export const GRANTABLE_ROLES = [
  'DIVER', 'APNEISTA', 'INSTRUCTOR', 'DIVEMASTER', 'DIVE_GUIDE', 'DIVE_DOCTOR',
  'DIVE_PHOTOGRAPHER', 'TECHNICAL_DIVER', 'RESEARCHER', 'TOURISM_OPERATOR', 'RENTAL_OPERATOR',
] as const

export const CONTACT_POINT_TYPES = ['EMAIL', 'PHONE', 'MOBILE', 'WHATSAPP', 'WEBSITE', 'LINKEDIN', 'FACEBOOK', 'INSTAGRAM', 'X_TWITTER', 'TELEGRAM', 'TIKTOK', 'YOUTUBE', 'OTHER'] as const
// Etichette leggibili per i tipi di contatto (il backend usa enum tecnici)
export const CONTACT_POINT_LABEL: Record<string, string> = {
  EMAIL: 'Email', PHONE: 'Telefono', MOBILE: 'Cellulare', WHATSAPP: 'WhatsApp', WEBSITE: 'Sito web',
  LINKEDIN: 'LinkedIn', FACEBOOK: 'Facebook', INSTAGRAM: 'Instagram', X_TWITTER: 'X (Twitter)',
  TELEGRAM: 'Telegram', TIKTOK: 'TikTok', YOUTUBE: 'YouTube', FAX: 'Fax', OTHER: 'Altro',
}

export const LIFECYCLE_STAGES: LifecycleStage[] = ['SUBSCRIBER', 'LEAD', 'MQL', 'SQL', 'OPPORTUNITY', 'CUSTOMER', 'EVANGELIST', 'INACTIVE', 'OTHER']
export const LIFECYCLE_LABEL: Record<LifecycleStage, string> = {
  SUBSCRIBER: 'Iscritto', LEAD: 'Lead', MQL: 'MQL', SQL: 'SQL', OPPORTUNITY: 'Opportunità', CUSTOMER: 'Cliente', EVANGELIST: 'Promotore', INACTIVE: 'Inattivo', OTHER: 'Altro',
}
export const CONSENT_CHANNELS: ConsentChannel[] = ['EMAIL', 'WHATSAPP', 'PHONE']
export const CONSENT_STATUS_LABEL: Record<ConsentStatus, { label: string; cls: string }> = {
  GRANTED: { label: 'Concesso', cls: 'bg-emerald-100 text-emerald-700' },
  DENIED:  { label: 'Negato',   cls: 'bg-red-100 text-red-700' },
  PENDING: { label: 'In attesa', cls: 'bg-amber-100 text-amber-700' },
  REVOKED: { label: 'Revocato',  cls: 'bg-slate-200 text-slate-600' },
}
export const DEAL_STAGES: DealStage[] = ['NEW', 'QUALIFIED', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST']
export const DEAL_STAGE_LABEL: Record<DealStage, string> = {
  NEW: 'Nuovo', QUALIFIED: 'Qualificato', PROPOSAL: 'Proposta', NEGOTIATION: 'Negoziazione', WON: 'Vinto', LOST: 'Perso',
}
